import { useState, useEffect, useRef, lazy, Suspense } from "react";

// lazy a proposito: la esfera queda en su propio chunk y solo se descarga al
// abrir esta pestania del admin. Ningun estudio se la baja.
const EsferaFlujo = lazy(() => import("./admin/EsferaFlujo"));

const API = process.env.REACT_APP_API_URL || "https://obras-backend-production.up.railway.app";

const C = {
  bg:"#f8f9fa", surface:"#ffffff", surface2:"#f1f3f5",
  border:"#e0e0e8", text:"#1a1a2e", muted:"#6b7280",
  accent:"#059669", accent2:"#7c3aed", warn:"#d97706",
  red:"#ef4444", green:"#10b981",
};

const fmt = n => "$ " + Math.round(n).toLocaleString("es-AR");

function Badge({ plan }) {
  const colors = {
    trial:     { bg:"#fffbeb", color:"#d97706", border:"#fde68a", label:"Trial" },
    activo:    { bg:"#f0fdf4", color:"#059669", border:"#bbf7d0", label:"Activo" },
    vencido:   { bg:"#fef2f2", color:"#ef4444", border:"#fecaca", label:"Vencido" },
    cancelado: { bg:"#f1f5f9", color:"#64748b", border:"#e2e8f0", label:"Cancelado" },
  };
  const s = colors[plan] || colors.cancelado;
  return <span style={{ fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:20, background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>{s.label}</span>;
}

const CATEGORIAS_TENANT = [
  { id:"normal",      label:"Normal" },
  { id:"tester",      label:"Tester" },
  { id:"promo",       label:"Promo" },
  { id:"condicional", label:"Condicional" },
];

// ── Panel de Mano de Obra ──────────────────────────────────────────────────────
const CS_REF = 65; // % cargas sociales referencia UOCRA construcción Argentina

function fmtFechaCorta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", { day:"2-digit", month:"2-digit", year:"numeric" });
}

// Una fecha basura en la base no puede tirar abajo el panel entero: toISOString()
// sobre un Date invalido lanza RangeError y React desmonta todo (pantalla en blanco).
function aISO(valor) {
  if (!valor) return "";
  const d = new Date(valor);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function PanelMO({ token }) {
  const [list, setList]     = useState([]);
  const [edit, setEdit]     = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved]   = useState({});
  const [csRef, setCsRef]   = useState(CS_REF);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    const r = await fetch(`${API}/admin/mo`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setList(await r.json());
  };

  // Usa el endpoint bulk que está confirmado funcionando
  const guardar = async (id, costo) => {
    setSaving(s => ({ ...s, [id]: true }));
    const r = await fetch(`${API}/admin/bulk-precios/mo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify([{ id, costo_hora: parseFloat(costo) }])
    });
    const ok2 = r.ok;
    setSaving(s => ({ ...s, [id]: false }));
    if (ok2) { setSaved(s => ({ ...s, [id]: true })); setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 1500); }
    else { alert("Error al guardar"); }
    cargar();
  };

  // Fecha de última actualización (la más reciente del conjunto)
  const lastUpdate = list.reduce((mx, m) => {
    if (!m.updated_at) return mx;
    return !mx || m.updated_at > mx ? m.updated_at : mx;
  }, null);

  return (
    <div>
      {/* Info cargas sociales */}
      <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#92400e" }}>
        <strong>Tasas base UOCRA Zona A.</strong> Las cargas sociales (jubilación, obra social, ART, SAC, vacaciones, etc.)
        se aplican <em>por presupuesto</em> en el cotizador — por defecto el 65% sobre el salario base.
        Costo empleador estimado = tasa base × (1 + CS%/100).
        <div style={{ marginTop:6, display:"flex", alignItems:"center", gap:8 }}>
          <span>CS% de referencia:</span>
          <input type="number" value={csRef} onChange={e => setCsRef(parseFloat(e.target.value)||0)} min={0} max={200}
            style={{ width:60, padding:"2px 6px", borderRadius:5, border:"1px solid #fde68a", fontSize:12, fontFamily:"'IBM Plex Mono',monospace" }} />
          <span style={{ color:"#78350f" }}>→ factor {(1 + csRef/100).toFixed(2)}x</span>
        </div>
      </div>

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 140px 140px 70px 110px", padding:"10px 16px", background:C.surface2, borderBottom:`1px solid ${C.border}` }}>
          {["Categoría","Grupo","Tasa base/hora","Con CS " + csRef + "%","","Actualizado"].map(h => (
            <div key={h} style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
          ))}
        </div>
        {list.map((m, i) => {
          const val = edit[m.id] !== undefined ? edit[m.id] : String(m.costo_hora);
          const base = parseFloat(val) || 0;
          const conCs = Math.round(base * (1 + csRef / 100));
          const changed = base !== m.costo_hora;
          return (
            <div key={m.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 140px 140px 70px 110px", padding:"10px 16px", borderBottom: i < list.length-1 ? `1px solid ${C.border}` : "none", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{m.nombre}</div>
              <div style={{ fontSize:12, color:C.muted }}>{m.categoria || "—"}</div>
              <input
                type="number" value={val}
                onChange={e => setEdit(prev => ({ ...prev, [m.id]: e.target.value }))}
                style={{ padding:"5px 8px", border:`1px solid ${changed ? C.accent : C.border}`, borderRadius:6, fontSize:13, fontFamily:"'IBM Plex Mono',monospace", width:"100%", outline:"none", boxSizing:"border-box" }}
              />
              <div style={{ fontSize:13, fontFamily:"'IBM Plex Mono',monospace", color:C.accent, fontWeight:600 }}>
                $ {conCs.toLocaleString("es-AR")}
              </div>
              <button
                onClick={() => guardar(m.id, parseFloat(val))}
                disabled={saving[m.id] || !changed}
                style={{ padding:"5px 10px", borderRadius:6, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", background: saved[m.id] ? C.green : changed ? C.accent : C.surface2, color: saved[m.id] || changed ? "white" : C.muted, opacity:saving[m.id]?0.6:1 }}>
                {saved[m.id] ? "✓" : saving[m.id] ? "..." : "OK"}
              </button>
              <div style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>
                {fmtFechaCorta(m.updated_at) || "—"}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize:12, color:C.muted, marginTop:8, fontFamily:"'IBM Plex Mono',monospace", display:"flex", justifyContent:"space-between" }}>
        <span>Fuente: UOCRA — Zona A. Actualizar mensualmente con el convenio vigente.</span>
        {lastUpdate && <span>Ultima actualización: {fmtFechaCorta(lastUpdate)}</span>}
      </div>
    </div>
  );
}

// ── Panel de Maquinaria ────────────────────────────────────────────────────────
function PanelMaquinaria({ token }) {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    const r = await fetch(`${API}/admin/maquinaria`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setList(await r.json());
  };

  const guardar = async (id, costo) => {
    setSaving(s => ({ ...s, [id]: true }));
    await fetch(`${API}/admin/bulk-precios/maquinaria`, {
      method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify([{ id, costo_hora: parseFloat(costo) }])
    });
    setSaving(s => ({ ...s, [id]: false }));
    setSaved(s => ({ ...s, [id]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 1500);
    cargar();
  };

  return (
    <div>
      <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>
        Costo horario de equipos y herramientas. Representa la amortización por hora de uso. Aplica a todos los estudios.
      </div>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 160px 80px", padding:"10px 16px", background:C.surface2, borderBottom:`1px solid ${C.border}` }}>
          {["Equipo / Herramienta","Costo/hora (ARS)",""].map(h => (
            <div key={h} style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
          ))}
        </div>
        {list.map((m, i) => {
          const val = edit[m.id] !== undefined ? edit[m.id] : String(m.costo_hora);
          const changed = parseFloat(val) !== m.costo_hora;
          return (
            <div key={m.id} style={{ display:"grid", gridTemplateColumns:"2fr 160px 80px", padding:"10px 16px", borderBottom: i < list.length-1 ? `1px solid ${C.border}` : "none", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{m.nombre}</div>
              <input
                type="number" value={val}
                onChange={e => setEdit(prev => ({ ...prev, [m.id]: e.target.value }))}
                style={{ padding:"6px 10px", border:`1px solid ${changed ? C.accent : C.border}`, borderRadius:6, fontSize:13, fontFamily:"'IBM Plex Mono',monospace", width:"100%", outline:"none", boxSizing:"border-box" }}
              />
              <button
                onClick={() => guardar(m.id, val)}
                disabled={saving[m.id] || !changed}
                style={{ padding:"5px 12px", borderRadius:6, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", background: saved[m.id] ? C.green : changed ? C.accent : C.surface2, color: saved[m.id] || changed ? "white" : C.muted, opacity:saving[m.id]?0.6:1 }}>
                {saved[m.id] ? "✓" : saving[m.id] ? "..." : "OK"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Panel Hierros Líder ───────────────────────────────────────────────────────
// Pesos estándar (kg/m) para hierro nervurado según norma CIRSOC 201
const PESO_KG_M = { "6": 0.222, "8": 0.395, "10": 0.617, "12": 0.888, "14": 1.208, "16": 1.578, "20": 2.466, "25": 3.853, "32": 6.313 };
const KG_BARRA = (mm) => (PESO_KG_M[mm] || 0) * 12;

function PanelHierros({ token }) {
  const [items, setItems]       = useState([]);
  const [edits, setEdits]       = useState({});
  const [saving, setSaving]     = useState({});
  const [saved, setSaved]       = useState({});
  const [fuente, setFuente]     = useState("Hierros Líder");
  const [fecha, setFecha]       = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult]     = useState(null);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    const r = await fetch(`${API}/admin/materiales?q=HYA&limit=50`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { const d = await r.json(); setItems(d.items || []); }
  };

  const setEdit = (id, val) => setEdits(p => ({ ...p, [id]: val }));

  // Extraer mm del nombre
  const getMm = (nombre) => {
    const m = nombre.match(/(\d+)\s*mm/i) || nombre.match(/[øØ]\s*(\d+)/);
    return m ? m[1] : null;
  };

  // Calcular precio/barra 12m para nervurado
  const precioBarra = (m) => {
    const mm = getMm(m.nombre);
    const kg = KG_BARRA(mm);
    if (!kg) return null;
    const precio = parseFloat(edits[m.id] ?? m.precio_unitario);
    return Math.round(precio * kg);
  };

  const guardar = async (id, precio) => {
    setSaving(s => ({ ...s, [id]: true }));
    const r = await fetch(`${API}/admin/materiales/${id}/precio`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ precio_unitario: parseFloat(precio), fuente_precio: fuente, fecha_precio: fecha })
    });
    setSaving(s => ({ ...s, [id]: false }));
    if (r.ok) { setSaved(s => ({ ...s, [id]: true })); setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 1500); }
    else { const e = await r.json().catch(() => ({})); alert(`Error ${r.status}: ${e.detail || r.statusText}`); }
    cargar();
  };

  // Guardar todos los modificados
  const guardarTodos = async () => {
    const changed = items.filter(m => edits[m.id] !== undefined && parseFloat(edits[m.id]) !== m.precio_unitario);
    if (!changed.length) { alert("Sin cambios"); return; }
    const payload = changed.map(m => ({
      id: m.id, precio_unitario: parseFloat(edits[m.id]),
      fuente_precio: fuente, fecha_precio: fecha
    }));
    const r = await fetch(`${API}/admin/bulk-precios/materiales`, {
      method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (r.ok) { const d = await r.json(); setResult(`✓ ${d.updated} actualizados`); setEdits({}); cargar(); }
    else { alert("Error al guardar"); }
  };

  // Separar en grupos
  const nervurado = items.filter(m => /nervurado|nervado|hierro\s+\d|dn|adn/i.test(m.nombre) || (m.codigo?.startsWith("HYA") && /^hierro\s+\d/i.test(m.nombre)));
  const liso      = items.filter(m => /liso/i.test(m.nombre));
  const angulo    = items.filter(m => /ángulo|angulo/i.test(m.nombre));
  const planchuela= items.filter(m => /planchuela/i.test(m.nombre));
  const otros     = items.filter(m => !nervurado.includes(m) && !liso.includes(m) && !angulo.includes(m) && !planchuela.includes(m));

  const Row = ({ m, showBarra }) => {
    const val = edits[m.id] !== undefined ? edits[m.id] : String(Math.round(m.precio_unitario));
    const changed = parseFloat(val) !== Math.round(m.precio_unitario);
    const mm = getMm(m.nombre);
    const barra = showBarra && mm ? precioBarra({ ...m, precio_unitario: val }) : null;
    const diasAnt = m.fecha_precio ? Math.floor((Date.now() - new Date(m.fecha_precio)) / 86400000) : null;
    const alertColor = diasAnt === null ? C.muted : diasAnt > 60 ? C.red : diasAnt > 30 ? C.warn : C.green;
    return (
      <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 130px 110px 70px 110px", padding:"8px 14px", borderBottom:`1px solid ${C.border}`, alignItems:"center" }}>
        <div>
          <div style={{ fontSize:13, color:C.text }}>{m.nombre}</div>
          {m.fuente_precio && <div style={{ fontSize:10, color:C.muted }}>{m.fuente_precio} {m.fecha_precio ? `· ${new Date(m.fecha_precio+'T12:00').toLocaleDateString('es-AR')}` : ''}</div>}
        </div>
        <div style={{ fontSize:11, color:C.muted }}>{m.unidad}</div>
        <input type="number" value={val}
          onChange={e => setEdit(m.id, e.target.value)}
          style={{ padding:"5px 8px", border:`1px solid ${changed ? C.accent : C.border}`, borderRadius:6, fontSize:12, fontFamily:"'IBM Plex Mono',monospace", width:"100%", outline:"none", boxSizing:"border-box" }} />
        <div style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace", textAlign:"right" }}>
          {barra ? `barra: $${barra.toLocaleString("es-AR")}` : ""}
        </div>
        <button onClick={() => guardar(m.id, val)} disabled={saving[m.id] || !changed}
          style={{ padding:"4px 10px", borderRadius:6, border:"none", fontSize:11, fontWeight:700, cursor:"pointer", background: saved[m.id] ? C.green : changed ? C.accent : C.surface2, color: saved[m.id] || changed ? "white" : C.muted, opacity:saving[m.id]?0.6:1 }}>
          {saved[m.id] ? "✓" : saving[m.id] ? "..." : "OK"}
        </button>
        <div style={{ fontSize:10, color:alertColor, fontFamily:"'IBM Plex Mono',monospace" }}>
          {diasAnt !== null ? `hace ${diasAnt}d` : "sin fecha"}
        </div>
      </div>
    );
  };

  const Section = ({ title, mats, showBarra }) => mats.length === 0 ? null : (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.8px", color:C.muted, padding:"8px 14px", background:C.surface2, borderBottom:`1px solid ${C.border}` }}>
        {title} ({mats.length})
      </div>
      {mats.sort((a,b) => { const na = parseFloat(getMm(a.nombre)||0); const nb=parseFloat(getMm(b.nombre)||0); return na-nb || a.nombre.localeCompare(b.nombre); }).map(m => <Row key={m.id} m={m} showBarra={showBarra} />)}
    </div>
  );

  return (
    <div>
      {/* Header fuente + fecha */}
      <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.accent }}>Fuente de precios</div>
          <input value={fuente} onChange={e => setFuente(e.target.value)}
            style={{ flex:1, minWidth:160, padding:"6px 10px", border:"1px solid #bbf7d0", borderRadius:6, fontSize:13, outline:"none", background:"white" }} />
          <div style={{ fontSize:13, color:C.muted }}>Fecha referencia</div>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding:"6px 10px", border:"1px solid #bbf7d0", borderRadius:6, fontSize:13, outline:"none", background:"white" }} />
          <a href="https://tienda.hierroslider.com/hierros/" target="_blank" rel="noreferrer"
            style={{ fontSize:12, color:"#0284c7", textDecoration:"none", padding:"6px 12px", border:"1px solid #bae6fd", borderRadius:6, background:"#f0f9ff", whiteSpace:"nowrap" }}>
            Abrir Hierros Líder →
          </a>
          <button onClick={guardarTodos}
            style={{ padding:"7px 16px", borderRadius:7, border:"none", background:C.accent, color:"white", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
            Guardar todos los cambios
          </button>
          {result && <span style={{ fontSize:13, color:C.green, fontWeight:700 }}>{result}</span>}
        </div>
        <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>
          Fuente y fecha se aplican a todos los precios que guardés en esta sesión. El precio/barra se calcula automáticamente usando pesos CIRSOC (kg/m × 12m).
        </div>
      </div>

      {/* Tabla encabezado */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 130px 110px 70px 110px", padding:"8px 14px", background:C.surface2, border:`1px solid ${C.border}`, borderRadius:"8px 8px 0 0" }}>
        {["Material","Unidad","Precio/unidad","Precio/barra 12m","","Antigüedad"].map(h => (
          <div key={h} style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
        ))}
      </div>

      <div style={{ border:`1px solid ${C.border}`, borderTop:"none", borderRadius:"0 0 8px 8px", overflow:"hidden" }}>
        <Section title="Nervurado / DN Construcción" mats={[...nervurado, ...otros.filter(m => /^hierro\s+\d/i.test(m.nombre))]} showBarra={true} />
        <Section title="Liso" mats={liso} showBarra={true} />
        <Section title="Ángulos" mats={angulo} showBarra={false} />
        <Section title="Planchuelas" mats={planchuela} showBarra={false} />
        <Section title="Otros" mats={otros.filter(m => !/^hierro\s+\d/i.test(m.nombre) && !/caño|tee/i.test(m.nombre))} showBarra={false} />
      </div>
    </div>
  );
}

// ── Panel de Materiales ────────────────────────────────────────────────────────
function PanelMateriales({ token }) {
  const [list, setList]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [search, setSearch]       = useState("");
  const [edit, setEdit]           = useState({});
  const [saving, setSaving]       = useState({});
  const [saved, setSaved]         = useState({});
  const [csvText, setCsvText]     = useState("");
  const [csvResult, setCsvResult] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [showCsv, setShowCsv]     = useState(false);
  const [page, setPage]           = useState(0);
  const fileRef = useRef();
  const PER_PAGE = 50;

  useEffect(() => { cargar(); }, [search, page]);

  const cargar = async () => {
    const q = search ? `&q=${encodeURIComponent(search)}` : "";
    const r = await fetch(`${API}/admin/materiales?limit=${PER_PAGE}&offset=${page * PER_PAGE}${q}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (r.ok) { const d = await r.json(); setList(d.items); setTotal(d.total); }
  };

  const guardarPrecio = async (id, precio) => {
    setSaving(s => ({ ...s, [id]: true }));
    const r = await fetch(`${API}/admin/materiales/${id}/precio`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ precio_unitario: parseFloat(precio) })
    });
    setSaving(s => ({ ...s, [id]: false }));
    if (r.ok) {
      setSaved(s => ({ ...s, [id]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 1500);
    } else {
      const err = await r.json().catch(() => ({}));
      alert(`Error ${r.status}: ${err.detail || r.statusText || "Intentá volver a ingresar al panel"}`);
    }
    cargar();
  };

  const parseCsv = (text) => {
    const lines = text.trim().split("\n").filter(Boolean);
    if (!lines.length) return [];
    const sep = lines[0].includes(";") ? ";" : ",";
    const header = lines[0].toLowerCase().split(sep).map(h => h.trim());
    const hasHeader = isNaN(parseFloat(lines[0].split(sep)[0]));
    const dataLines = hasHeader ? lines.slice(1) : lines;
    return dataLines.map(line => {
      const parts = line.split(sep).map(p => p.trim().replace(/^["']|["']$/g, ""));
      if (hasHeader) {
        const obj = {};
        header.forEach((h, i) => obj[h] = parts[i]);
        return {
          codigo: obj.codigo || obj.code || obj["cód"] || null,
          nombre: obj.nombre || obj.name || obj.material || null,
          precio_unitario: parseFloat(obj.precio_unitario || obj.precio || obj.price || 0),
        };
      } else {
        return { codigo: parts[0] || null, precio_unitario: parseFloat(parts[1] || 0) };
      }
    }).filter(r => r.precio_unitario > 0);
  };

  const importarCsv = async () => {
    const items = parseCsv(csvText);
    if (!items.length) { alert("No se encontraron datos válidos en el CSV"); return; }
    setCsvLoading(true);
    const r = await fetch(`${API}/admin/bulk-precios/materiales`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(items)
    });
    const d = await r.json();
    setCsvResult(d);
    setCsvLoading(false);
    cargar();
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setCsvText(ev.target.result);
    reader.readAsText(file, "UTF-8");
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:13, color:C.muted }}>
          {total.toLocaleString("es-AR")} materiales en el catálogo global. Los precios aplican a todos los estudios (salvo override por tenant).
        </div>
        <button onClick={() => setShowCsv(!showCsv)}
          style={{ padding:"7px 14px", borderRadius:8, border:`1px solid ${C.border}`, background:showCsv ? C.accent2 : C.surface, color:showCsv ? "white" : C.text, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          {showCsv ? "× Cerrar import" : "⬆ Importar CSV / DataObra"}
        </button>
      </div>

      {showCsv && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:8 }}>Importar precios desde CSV</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>
            Formato: <code style={{ background:C.surface2, padding:"2px 6px", borderRadius:4 }}>codigo;precio_unitario</code> ó <code style={{ background:C.surface2, padding:"2px 6px", borderRadius:4 }}>nombre;precio_unitario</code><br/>
            También acepta Excel exportado como CSV. Separador: <code style={{ background:C.surface2, padding:"2px 6px", borderRadius:4 }}>,</code> ó <code style={{ background:C.surface2, padding:"2px 6px", borderRadius:4 }}>;</code>
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:10 }}>
            <button onClick={() => fileRef.current?.click()}
              style={{ padding:"7px 14px", borderRadius:7, border:`1px solid ${C.border}`, background:C.surface2, fontSize:13, cursor:"pointer", color:C.text }}>
              Seleccionar archivo CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} style={{ display:"none" }} />
            <span style={{ fontSize:12, color:C.muted, alignSelf:"center" }}>o pegá el contenido abajo</span>
          </div>
          <textarea
            value={csvText} onChange={e => { setCsvText(e.target.value); setCsvResult(null); }}
            placeholder={"codigo;precio_unitario\nAGL-004;1200\nAGL-025;450\nCEM-001;9500"}
            style={{ width:"100%", height:160, padding:10, border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, fontFamily:"'IBM Plex Mono',monospace", boxSizing:"border-box", resize:"vertical", outline:"none" }}
          />
          {csvText.trim() && (
            <div style={{ fontSize:12, color:C.muted, margin:"6px 0" }}>
              {parseCsv(csvText).length} registros detectados
            </div>
          )}
          <div style={{ display:"flex", gap:10, marginTop:10, alignItems:"center" }}>
            <button onClick={importarCsv} disabled={csvLoading || !csvText.trim()}
              style={{ padding:"8px 20px", background:C.accent, color:"white", border:"none", borderRadius:8, fontSize:14, fontWeight:700, cursor:"pointer", opacity:csvLoading?0.6:1 }}>
              {csvLoading ? "Importando..." : "Importar precios"}
            </button>
            {csvResult && (
              <div style={{ fontSize:13 }}>
                <span style={{ color:C.green, fontWeight:700 }}>✓ {csvResult.updated} actualizados</span>
                {csvResult.not_found?.length > 0 && (
                  <span style={{ color:C.warn, marginLeft:12 }}>⚠ {csvResult.not_found.length} no encontrados: {csvResult.not_found.slice(0,3).join(", ")}{csvResult.not_found.length>3?"...":""}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Búsqueda */}
      <div style={{ display:"flex", gap:10, marginBottom:12 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Buscar por código o nombre..."
          style={{ flex:1, padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:C.surface, outline:"none" }} />
        {total > PER_PAGE && (
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page===0}
              style={{ padding:"6px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, cursor:"pointer", fontSize:13, opacity:page===0?0.4:1 }}>←</button>
            <span style={{ fontSize:12, color:C.muted }}>{page+1} / {Math.ceil(total/PER_PAGE)}</span>
            <button onClick={() => setPage(p => p+1)} disabled={(page+1)*PER_PAGE >= total}
              style={{ padding:"6px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, cursor:"pointer", fontSize:13, opacity:(page+1)*PER_PAGE>=total?0.4:1 }}>→</button>
          </div>
        )}
      </div>

      {/* Tabla materiales */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"90px 1fr 50px 150px 160px 70px", padding:"10px 16px", background:C.surface2, borderBottom:`1px solid ${C.border}` }}>
          {["Código","Nombre","Ud.","Precio (ARS)","Fuente · Fecha",""].map(h => (
            <div key={h} style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
          ))}
        </div>
        {list.map((m, i) => {
          const val = edit[m.id] !== undefined ? edit[m.id] : String(Math.round(m.precio_unitario));
          const changed = parseFloat(val) !== Math.round(m.precio_unitario);
          const diasAnt = m.fecha_precio ? Math.floor((Date.now() - new Date(m.fecha_precio)) / 86400000) : null;
          const ageColor = diasAnt === null ? C.muted : diasAnt > 60 ? C.red : diasAnt > 30 ? C.warn : C.green;
          return (
            <div key={m.id} style={{ display:"grid", gridTemplateColumns:"90px 1fr 50px 150px 160px 70px", padding:"8px 16px", borderBottom: i < list.length-1 ? `1px solid ${C.border}` : "none", alignItems:"center" }}>
              <div style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>{m.codigo}</div>
              <div style={{ fontSize:13, color:C.text }}>{m.nombre}</div>
              <div style={{ fontSize:12, color:C.muted }}>{m.unidad}</div>
              <input
                type="number" value={val}
                onChange={e => setEdit(prev => ({ ...prev, [m.id]: e.target.value }))}
                style={{ padding:"5px 8px", border:`1px solid ${changed ? C.accent : C.border}`, borderRadius:6, fontSize:12, fontFamily:"'IBM Plex Mono',monospace", width:"100%", outline:"none", boxSizing:"border-box" }}
              />
              <div style={{ fontSize:10, color:ageColor, lineHeight:1.4 }}>
                <div>{m.fuente_precio || <span style={{ color:C.muted }}>—</span>}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace" }}>
                  {m.fecha_precio ? new Date(m.fecha_precio+'T12:00').toLocaleDateString('es-AR') : "sin fecha"}
                  {diasAnt !== null && <span> ({diasAnt}d)</span>}
                </div>
              </div>
              <button
                onClick={() => guardarPrecio(m.id, val)}
                disabled={saving[m.id] || !changed}
                style={{ padding:"4px 10px", borderRadius:6, border:"none", fontSize:11, fontWeight:700, cursor:"pointer", background: saved[m.id] ? C.green : changed ? C.accent : C.surface2, color: saved[m.id] || changed ? "white" : C.muted, opacity:saving[m.id]?0.6:1 }}>
                {saved[m.id] ? "✓" : saving[m.id] ? "..." : "OK"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Panel de Anuncio global ─────────────────────────────────────────────────────
function PanelAnuncio({ token }) {
  const [titulo, setTitulo] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [activo, setActivo] = useState(true);
  const [notificar, setNotificar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { cargar(); }, []);
  const cargar = async () => {
    const r = await fetch(`${API}/admin/anuncio`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const d = await r.json();
      if (d) { setTitulo(d.titulo || ""); setMensaje(d.mensaje || ""); setActivo(!!d.activo); setNotificar(false); }
    }
  };
  const guardar = async () => {
    setSaving(true); setMsg(null);
    const r = await fetch(`${API}/admin/anuncio`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ titulo, mensaje, activo, notificar }),
    });
    setSaving(false);
    setMsg(r.ok ? "✓ Guardado. Los usuarios lo verán la próxima vez que entren." : "Error al guardar");
    if (r.ok) setTimeout(() => setMsg(null), 4000);
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
        Mensaje que aparece como banner arriba para <strong>todos los estudios</strong>. Cada usuario lo puede cerrar; si publicás uno nuevo, vuelve a aparecer.
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Título (opcional)</label>
        <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Novedad"
          style={{ width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, marginTop: 6, marginBottom: 14, boxSizing: "border-box", outline: "none" }} />
        <label style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Mensaje</label>
        <textarea value={mensaje} onChange={e => setMensaje(e.target.value)} placeholder="Ej: ¡Nuevo! Ahora el asistente responde consultas sobre tus presupuestos: precios, materiales, cobros y certificados."
          style={{ width: "100%", height: 90, padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, marginTop: 6, marginBottom: 14, boxSizing: "border-box", outline: "none", resize: "vertical", fontFamily: "inherit" }} />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: C.text, marginBottom: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)} /> Mostrar el anuncio (destildá para ocultarlo)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: C.text, marginBottom: 16, cursor: "pointer" }}>
          <input type="checkbox" checked={notificar} onChange={e => setNotificar(e.target.checked)} />
          Mandar también como notificación push a todos los estudios (usalo solo para lo importante)
        </label>
        {mensaje && activo && (
          <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", color: "#3730a3", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 16 }}>
            Vista previa: {titulo && <strong>{titulo} </strong>}{mensaje}
          </div>
        )}
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={guardar} disabled={saving} style={{ padding: "9px 20px", background: C.accent, color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Guardando..." : "Publicar anuncio"}
          </button>
          {msg && <span style={{ fontSize: 13, color: msg.startsWith("✓") ? C.green : C.red, fontWeight: 600 }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

const MEDIOS_PAGO = [["transferencia","Transferencia"],["mercadopago","MercadoPago"],["otro","Otro"]];
const hoyISO = () => new Date().toISOString().slice(0,10);

// Plata del NEGOCIO FAIM OBRAS — lo que cobran los estudios (transferencia o
// MercadoPago, cargado a mano acá) contra lo que se gasta en publicidad,
// herramientas, etc. Nada de esto es de ningún tenant.
function PanelFinanzas({ token, tenants }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState({ tipo: "ingreso", concepto: "", monto: "", medio_pago: "mercadopago", tenant_id: "", fecha: hoyISO(), nota: "" });
  const [guardando, setGuardando] = useState(false);
  const [rango, setRango] = useState({ desde: "", hasta: "" });

  const cargar = async () => {
    setCargando(true);
    const q = [];
    if (rango.desde) q.push(`desde=${rango.desde}`);
    if (rango.hasta) q.push(`hasta=${rango.hasta}`);
    const r = await fetch(`${API}/admin/movimientos${q.length ? "?" + q.join("&") : ""}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setDatos(await r.json());
    setCargando(false);
  };
  useEffect(() => { cargar(); }, [rango.desde, rango.hasta]); // eslint-disable-line

  const guardar = async () => {
    if (!form.concepto.trim() || !form.monto) return;
    setGuardando(true);
    try {
      await fetch(`${API}/admin/movimientos`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, monto: parseFloat(form.monto) || 0, tenant_id: form.tenant_id || null }),
      });
      setForm(f => ({ ...f, concepto: "", monto: "", tenant_id: "", nota: "" }));
      cargar();
    } catch {}
    setGuardando(false);
  };

  const eliminar = async (mid) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    await fetch(`${API}/admin/movimientos/${mid}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    cargar();
  };

  const inp = { width:"100%", padding:"8px 10px", border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, background:C.surface2, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const lbl = { fontSize:10.5, color:C.muted, textTransform:"uppercase", letterSpacing:"0.4px", display:"block", marginBottom:4 };

  return (
    <div>
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"flex-end" }}>
        <div>
          <label style={lbl}>Desde</label>
          <input type="date" style={inp} value={rango.desde} onChange={e=>setRango(r=>({...r, desde:e.target.value}))} />
        </div>
        <div>
          <label style={lbl}>Hasta</label>
          <input type="date" style={inp} value={rango.hasta} onChange={e=>setRango(r=>({...r, hasta:e.target.value}))} />
        </div>
        {(rango.desde || rango.hasta) && (
          <button onClick={()=>setRango({desde:"",hasta:""})} style={{ padding:"8px 14px", background:"none", border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, color:C.muted, cursor:"pointer" }}>Ver todo</button>
        )}
      </div>

      {datos?.totales && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
          {[
            { label:"Ingresos", value:datos.totales.ingresos, color:C.green },
            { label:"Gastos", value:datos.totales.egresos, color:C.red },
            { label:"Balance", value:datos.totales.balance, color:datos.totales.balance >= 0 ? C.accent : C.red },
          ].map(s => (
            <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px" }}>
              <div style={{ fontSize:20, fontWeight:800, color:s.color, fontFamily:"'IBM Plex Mono',monospace" }}>{fmt(s.value)}</div>
              <div style={{ fontSize:11, color:C.muted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px" }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>Cargar movimiento</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:10 }}>
          <div>
            <label style={lbl}>Tipo</label>
            <select style={inp} value={form.tipo} onChange={e=>setForm(f=>({...f, tipo:e.target.value}))}>
              <option value="ingreso">Ingreso</option>
              <option value="egreso">Gasto</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Monto</label>
            <input type="number" style={inp} value={form.monto} onChange={e=>setForm(f=>({...f, monto:e.target.value}))} />
          </div>
          <div>
            <label style={lbl}>Fecha</label>
            <input type="date" style={inp} value={form.fecha} onChange={e=>setForm(f=>({...f, fecha:e.target.value}))} />
          </div>
          {form.tipo === "ingreso" && (
            <div>
              <label style={lbl}>Medio de pago</label>
              <select style={inp} value={form.medio_pago} onChange={e=>setForm(f=>({...f, medio_pago:e.target.value}))}>
                {MEDIOS_PAGO.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1.4fr", gap:10, marginBottom:10 }}>
          <div>
            <label style={lbl}>Concepto</label>
            <input style={inp} placeholder={form.tipo === "ingreso" ? "Cuota mensual, alta, etc." : "Publicidad Instagram, suscripción Claude, etc."}
              value={form.concepto} onChange={e=>setForm(f=>({...f, concepto:e.target.value}))} />
          </div>
          {form.tipo === "ingreso" && (
            <div>
              <label style={lbl}>Estudio (opcional)</label>
              <select style={inp} value={form.tenant_id} onChange={e=>setForm(f=>({...f, tenant_id:e.target.value}))}>
                <option value="">Sin asociar</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
        <button onClick={guardar} disabled={guardando || !form.concepto.trim() || !form.monto}
          style={{ padding:"9px 20px", background:C.accent, color:"white", border:"none", borderRadius:8, fontSize:14, fontWeight:700, cursor:"pointer", opacity: guardando ? 0.6 : 1 }}>
          {guardando ? "Guardando..." : "Agregar"}
        </button>
      </div>

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"0.9fr 2fr 1fr 1fr 1.2fr auto", gap:0, borderBottom:`1px solid ${C.border}`, padding:"10px 16px", background:C.surface2 }}>
          {["Fecha","Concepto","Medio","Monto","Estudio","Acciones"].map(h => (
            <div key={h} style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
          ))}
        </div>
        {cargando ? (
          <div style={{ padding:32, textAlign:"center", color:C.muted }}>Cargando...</div>
        ) : !datos?.movimientos?.length ? (
          <div style={{ padding:32, textAlign:"center", color:C.muted }}>Sin movimientos cargados</div>
        ) : datos.movimientos.map((m, i) => (
          <div key={m.id} style={{ display:"grid", gridTemplateColumns:"0.9fr 2fr 1fr 1fr 1.2fr auto", gap:0, padding:"10px 16px", borderBottom: i < datos.movimientos.length-1 ? `1px solid ${C.border}` : "none", alignItems:"center" }}>
            <div style={{ fontSize:12, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>{m.fecha}</div>
            <div style={{ fontSize:13 }}>{m.concepto}{m.nota ? <span style={{ color:C.muted }}> — {m.nota}</span> : null}</div>
            <div style={{ fontSize:12, color:C.muted, textTransform:"capitalize" }}>{m.medio_pago || "—"}</div>
            <div style={{ fontSize:13, fontWeight:700, fontFamily:"'IBM Plex Mono',monospace", color: m.tipo === "ingreso" ? C.green : C.red }}>
              {m.tipo === "ingreso" ? "+" : "−"}{fmt(m.monto)}
            </div>
            <div style={{ fontSize:12, color:C.muted }}>{m.tenant_nombre || "—"}</div>
            <button onClick={()=>eliminar(m.id)} style={{ padding:"4px 10px", fontSize:11, fontWeight:700, background:"#fef2f2", border:"1px solid #fecaca", color:C.red, borderRadius:6, cursor:"pointer" }}>
              Eliminar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bot WhatsApp ────────────────────────────────────────────────────────────
// Leads comerciales de FAIM OBRAS (no son de ningún tenant). El backend los
// protege con get_admin_user, no con get_tenant_id.
const ESTADOS_LEAD_BOT = ["nuevo", "calificando", "cotizado", "esperando_pago", "cliente", "perdido", "derivado"];

function BadgeEstadoLead({ estado }) {
  const colors = {
    nuevo:          { bg:"#eef2ff", color:"#4338ca", border:"#c7d2fe" },
    calificando:    { bg:"#fffbeb", color:"#d97706", border:"#fde68a" },
    cotizado:       { bg:"#f5f3ff", color:"#7c3aed", border:"#ddd6fe" },
    esperando_pago: { bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
    cliente:        { bg:"#f0fdf4", color:"#059669", border:"#bbf7d0" },
    perdido:        { bg:"#f1f5f9", color:"#64748b", border:"#e2e8f0" },
    derivado:       { bg:"#fef2f2", color:"#ef4444", border:"#fecaca" },
  };
  const s = colors[estado] || colors.nuevo;
  return <span style={{ fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:20, background:s.bg, color:s.color, border:`1px solid ${s.border}`, textTransform:"capitalize" }}>{estado?.replace("_"," ")}</span>;
}

function fmtHora(iso) {
  if (!iso) return "";
  // El backend manda el datetime sin zona (naive UTC, como lo guarda Postgres
  // con server_default=func.now()). Sin la "Z", new Date() lo toma como hora
  // local y el horario del bot queda mostrado 3hs adelantado.
  const conZona = /[Zz]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";
  const d = new Date(conZona);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("es-AR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
}

function PanelBotWhatsApp({ token }) {
  const [leads, setLeads]           = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [busqueda, setBusqueda]     = useState("");
  const [seleccionado, setSeleccionado] = useState(null); // detalle del lead abierto
  const [loadingLista, setLoadingLista] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [textoHumano, setTextoHumano] = useState("");
  const [enviando, setEnviando]     = useState(false);
  const chatRef = useRef(null);          // contenedor con scroll de la conversación
  const ultimoLeadScrolleado = useRef(null);
  const cantMensajesPrevia = useRef(0);

  useEffect(() => { cargarLista(); }, [filtroEstado]);

  // Como en WhatsApp: al abrir una conversación se ve el último mensaje. Cuando entra uno
  // nuevo (polling) baja solo, salvo que el usuario esté leyendo más arriba.
  useEffect(() => {
    const el = chatRef.current;
    if (!el || !seleccionado) return;
    const cant = seleccionado.mensajes?.length || 0;
    const abrioOtro = ultimoLeadScrolleado.current !== seleccionado.id;
    const cercaDelFinal = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (abrioOtro || (cant > cantMensajesPrevia.current && cercaDelFinal)) {
      el.scrollTop = el.scrollHeight;
    }
    ultimoLeadScrolleado.current = seleccionado.id;
    cantMensajesPrevia.current = cant;
  }, [seleccionado]);

  // Polling: la lista de leads y la conversación abierta se refrescan solas,
  // para no depender de que alguien recargue la página a mano para ver un
  // mensaje nuevo entrar.
  useEffect(() => {
    const t = setInterval(() => { cargarLista(true); }, 8000);
    return () => clearInterval(t);
  }, [filtroEstado]);

  useEffect(() => {
    if (!seleccionado) return;
    const t = setInterval(() => { abrirLead(seleccionado.id, true); }, 5000);
    return () => clearInterval(t);
  }, [seleccionado?.id]);

  const cargarLista = async (silencioso) => {
    if (!silencioso) setLoadingLista(true);
    try {
      const qs = filtroEstado !== "todos" ? `?estado=${filtroEstado}` : "";
      const r = await fetch(`${API}/whatsapp/admin/leads${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setLeads(await r.json());
    } catch {}
    if (!silencioso) setLoadingLista(false);
  };

  const abrirLead = async (id, silencioso) => {
    if (!silencioso) setLoadingDetalle(true);
    try {
      const r = await fetch(`${API}/whatsapp/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setSeleccionado(await r.json());
    } catch {}
    if (!silencioso) setLoadingDetalle(false);
  };

  // Borra un mensaje del panel y de la memoria del bot. No lo saca del WhatsApp del
  // cliente: la API de WhatsApp Business no permite "eliminar para todos".
  const borrarMensaje = async (m) => {
    if (!m.id) return;
    if (!window.confirm("¿Borrar este mensaje del panel y de la memoria del bot?\n\nOjo: al cliente le sigue apareciendo en su WhatsApp (WhatsApp no permite borrarlo desde la API).")) return;
    try {
      const r = await fetch(`${API}/whatsapp/admin/mensajes/${m.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setSeleccionado(s => s ? { ...s, mensajes: s.mensajes.filter(x => x.id !== m.id) } : s);
      } else {
        alert("No se pudo borrar el mensaje");
      }
    } catch { alert("Error de conexión"); }
  };

  const refrescarDetalle = () => { if (seleccionado) abrirLead(seleccionado.id); };

  const enviarComoHumano = async () => {
    if (!textoHumano.trim() || !seleccionado) return;
    setEnviando(true);
    try {
      const r = await fetch(`${API}/whatsapp/admin/leads/${seleccionado.id}/mensaje`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ texto: textoHumano.trim() }),
      });
      if (r.ok) { setTextoHumano(""); await refrescarDetalle(); await cargarLista(); }
      else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || "No se pudo enviar el mensaje");
      }
    } catch { alert("Error de conexión"); }
    setEnviando(false);
  };

  // Los archivos se sirven con el token de admin, así que no alcanza con un <a href>:
  // se bajan como blob y se abren en una pestaña nueva.
  const abrirArchivo = async (archivoId) => {
    const ventana = window.open("", "_blank");
    try {
      const r = await fetch(`${API}/whatsapp/admin/archivos/${archivoId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { ventana?.close(); alert("No se pudo abrir el archivo"); return; }
      const url = URL.createObjectURL(await r.blob());
      if (ventana) ventana.location.href = url; else window.open(url, "_blank");
    } catch { ventana?.close(); alert("Error de conexión"); }
  };

  const togglePausa = async () => {
    if (!seleccionado) return;
    const accion = seleccionado.bot_activo ? "pausar" : "reactivar";
    await fetch(`${API}/whatsapp/admin/leads/${seleccionado.id}/${accion}`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}` },
    });
    await refrescarDetalle();
    await cargarLista();
  };

  const cambiarEstadoLead = async (estado) => {
    if (!seleccionado) return;
    await fetch(`${API}/whatsapp/admin/leads/${seleccionado.id}/estado`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
    await refrescarDetalle();
    await cargarLista();
  };

  const leadsFiltrados = leads.filter(l => {
    if (!busqueda.trim()) return true;
    const q = busqueda.trim().toLowerCase();
    return (l.nombre || "").toLowerCase().includes(q) || (l.telefono || "").includes(q) || (l.email || "").toLowerCase().includes(q);
  });

  return (
    <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
      {/* Lista de leads */}
      <div style={{ width:320, flexShrink:0, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:12, borderBottom:`1px solid ${C.border}`, display:"flex", flexDirection:"column", gap:8 }}>
          <input value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Buscar por nombre, tel o email..."
            style={{ padding:"8px 10px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:"none" }} />
          <select value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}
            style={{ padding:"8px 10px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:"none", cursor:"pointer" }}>
            <option value="todos">Todos los estados</option>
            {ESTADOS_LEAD_BOT.map(e => <option key={e} value={e}>{e.replace("_"," ")}</option>)}
          </select>
        </div>
        <div style={{ maxHeight:560, overflowY:"auto" }}>
          {loadingLista ? (
            <div style={{ padding:20, textAlign:"center", color:C.muted, fontSize:13 }}>Cargando...</div>
          ) : leadsFiltrados.length === 0 ? (
            <div style={{ padding:20, textAlign:"center", color:C.muted, fontSize:13 }}>Sin leads</div>
          ) : leadsFiltrados.map(l => (
            <div key={l.id} onClick={()=>abrirLead(l.id)}
              style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, cursor:"pointer",
                background: seleccionado?.id === l.id ? C.surface2 : "transparent" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{l.nombre || l.telefono}</span>
                {!l.bot_activo && <span title="Bot pausado" style={{ fontSize:11 }}>⏸</span>}
              </div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{l.telefono}</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:6 }}>
                <BadgeEstadoLead estado={l.estado} />
                <span style={{ fontSize:10, color:C.muted }}>{fmtHora(l.ultimo_mensaje_at)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detalle / conversación */}
      <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, minHeight:400 }}>
        {!seleccionado ? (
          <div style={{ padding:40, textAlign:"center", color:C.muted, fontSize:13 }}>Elegí un lead de la lista para ver la conversación</div>
        ) : loadingDetalle ? (
          <div style={{ padding:40, textAlign:"center", color:C.muted, fontSize:13 }}>Cargando...</div>
        ) : (
          <>
            <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontWeight:800, fontSize:15, color:C.text }}>{seleccionado.nombre || "Sin nombre"}</div>
                <div style={{ fontSize:12, color:C.muted }}>{seleccionado.telefono}{seleccionado.email ? ` · ${seleccionado.email}` : ""}</div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <select value={seleccionado.estado} onChange={e=>cambiarEstadoLead(e.target.value)}
                  style={{ padding:"6px 10px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, cursor:"pointer" }}>
                  {ESTADOS_LEAD_BOT.map(e => <option key={e} value={e}>{e.replace("_"," ")}</option>)}
                </select>
                <button onClick={togglePausa}
                  style={{ padding:"6px 12px", fontSize:12, fontWeight:700, borderRadius:8, cursor:"pointer",
                    background: seleccionado.bot_activo ? "#fef2f2" : "#f0fdf4",
                    color: seleccionado.bot_activo ? C.red : C.green,
                    border: `1px solid ${seleccionado.bot_activo ? "#fecaca" : "#bbf7d0"}` }}>
                  {seleccionado.bot_activo ? "⏸ Pausar bot" : "▶ Reactivar bot"}
                </button>
              </div>
            </div>

            {seleccionado.archivos?.length > 0 && (
              <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, color:C.muted, fontWeight:700 }}>Archivos:</span>
                {seleccionado.archivos.map((a) => (
                  <button key={a.id} onClick={() => abrirArchivo(a.id)}
                    style={{ fontSize:12, color:C.accent2, border:"none", cursor:"pointer", fontWeight: a.tipo === "comprobante" ? 700 : 400,
                      background:C.surface2, padding:"3px 8px", borderRadius:6 }}>
                    📎 {a.tipo === "comprobante" ? "Comprobante" : (a.nombre_archivo || "Adjunto")}
                  </button>
                ))}
              </div>
            )}

            <div ref={chatRef} style={{ padding:16, display:"flex", flexDirection:"column", gap:10, maxHeight:380, overflowY:"auto" }}>
              {seleccionado.mensajes.length === 0 ? (
                <div style={{ color:C.muted, fontSize:13, textAlign:"center" }}>Todavía no hay mensajes</div>
              ) : seleccionado.mensajes.map((m,i) => {
                const deLead = m.direccion === "in";
                return (
                  <div key={m.id || i} style={{ display:"flex", alignItems:"center", gap:6, justifyContent: deLead ? "flex-start" : "flex-end" }}>
                    {!deLead && m.id && (
                      <button onClick={() => borrarMensaje(m)} title="Borrar mensaje"
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:C.muted, padding:2, opacity:0.6 }}>🗑</button>
                    )}
                    <div style={{ maxWidth:"70%", padding:"8px 12px", borderRadius:12, fontSize:13,
                      background: deLead ? C.surface2 : (m.autor === "humano" ? "#eef2ff" : "#f0fdf4"),
                      color: C.text }}>
                      {m.tipo !== "texto" && <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", marginBottom:2 }}>{m.tipo}{m.autor === "humano" ? " · humano" : m.autor === "bot" ? " · bot" : ""}</div>}
                      <div>{m.contenido || (m.media_url ? "[archivo adjunto]" : "")}</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:4, textAlign:"right" }}>{fmtHora(m.created_at)}</div>
                    </div>
                    {deLead && m.id && (
                      <button onClick={() => borrarMensaje(m)} title="Borrar mensaje"
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:C.muted, padding:2, opacity:0.6 }}>🗑</button>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ padding:12, borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
              <input value={textoHumano} onChange={e=>setTextoHumano(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") enviarComoHumano(); }}
                placeholder="Responder como humano (esto no pausa el bot — pausalo con el botón de arriba si no querés que siga respondiendo)..."
                style={{ flex:1, padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:"none" }} />
              <button onClick={enviarComoHumano} disabled={enviando || !textoHumano.trim()}
                style={{ padding:"9px 18px", background:C.accent, color:"white", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", opacity: enviando ? 0.6 : 1 }}>
                Enviar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [tab, setTab]               = useState("cuentas");
  const [preciosTab, setPreciosTab] = useState("mo");
  const [tenants, setTenants]       = useState([]);
  const [stats, setStats]           = useState(null);
  const [demoStats, setDemoStats]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [token, setToken]           = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass]   = useState("");
  const [loginError, setLoginError] = useState("");
  const [search, setSearch]         = useState("");
  const [filterPlan, setFilterPlan] = useState("todos");
  const [filterCategoria, setFilterCategoria] = useState("todas");
  const [precioEdit, setPrecioEdit] = useState({}); // { [tid]: valor }
  const [precioSaving, setPrecioSaving] = useState({});
  const [precioSaved, setPrecioSaved]   = useState({});
  const [usuariosEdit, setUsuariosEdit] = useState({}); // { [tid]: valor } — cupos de usuarios incluidos
  const [usuariosSaving, setUsuariosSaving] = useState({});
  const [usuariosSaved, setUsuariosSaved]   = useState({});

  useEffect(() => {
    const t = localStorage.getItem("obras_admin_token");
    if (t) { setToken(t); cargar(t); }
    else setLoading(false);
  }, []);

  const cargar = async (t) => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/admin/tenants`, { headers: { Authorization: `Bearer ${t}` } }),
        fetch(`${API}/admin/stats`,   { headers: { Authorization: `Bearer ${t}` } }),
        fetch(`${API}/admin/demo/estadisticas`, { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      if (r1.status === 401 || r2.status === 401) {
        // Sesión expirada — limpiar y pedir relogin
        localStorage.removeItem("obras_admin_token");
        setToken(null); setTenants([]); setStats(null); setDemoStats(null);
        setLoading(false);
        return;
      }
      if (r1.ok) setTenants(await r1.json());
      if (r2.ok) setStats(await r2.json());
      if (r3.ok) setDemoStats(await r3.json());
    } catch {}
    setLoading(false);
  };

  const loginAdmin = async () => {
    setLoginError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPass })
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("obras_admin_token", data.token);
        setToken(data.token);
        cargar(data.token);
      } else setLoginError("Credenciales incorrectas");
    } catch { setLoginError("Error de conexión"); }
  };

  const cambiarPlan = async (tid, plan) => {
    await fetch(`${API}/admin/tenants/${tid}/plan?plan=${plan}`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}` }
    });
    cargar(token);
  };

  const cambiarCategoria = async (tid, categoria) => {
    await fetch(`${API}/admin/tenants/${tid}/categoria`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ categoria }),
    });
    cargar(token);
  };

  const guardarPrecio = async (tid, precio) => {
    setPrecioSaving(s => ({ ...s, [tid]: true }));
    try {
      const res = await fetch(`${API}/admin/tenants/${tid}/precio`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ precio_mensual: parseFloat(precio) || 0 }),
      });
      if (res.ok) {
        setPrecioSaved(s => ({ ...s, [tid]: true }));
        setTimeout(() => setPrecioSaved(s => ({ ...s, [tid]: false })), 1500);
        setPrecioEdit(p => { const n = { ...p }; delete n[tid]; return n; });
        await cargar(token);
      } else {
        const e = await res.json().catch(() => ({}));
        alert("Error al guardar el precio: " + (e.detail || res.status));
      }
    } catch { alert("Error de conexión"); }
    setPrecioSaving(s => ({ ...s, [tid]: false }));
  };

  // Cupos de usuarios incluidos en el plan (el excedente se cobra como usuario extra;
  // no bloquea la creación de usuarios, solo cambia lo que el estudio ve en Suscripción).
  const guardarUsuarios = async (tid, incluidos) => {
    setUsuariosSaving(s => ({ ...s, [tid]: true }));
    try {
      const res = await fetch(`${API}/admin/tenants/${tid}/precio`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ usuarios_incluidos: parseInt(incluidos) || 0 }),
      });
      if (res.ok) {
        setUsuariosSaved(s => ({ ...s, [tid]: true }));
        setTimeout(() => setUsuariosSaved(s => ({ ...s, [tid]: false })), 1500);
        setUsuariosEdit(p => { const n = { ...p }; delete n[tid]; return n; });
        await cargar(token);
      } else {
        const e = await res.json().catch(() => ({}));
        alert("Error al guardar los cupos de usuarios: " + (e.detail || res.status));
      }
    } catch { alert("Error de conexión"); }
    setUsuariosSaving(s => ({ ...s, [tid]: false }));
  };

  const guardarTrial = async (tid, fecha) => {
    if (!fecha) return;
    try {
      const res = await fetch(`${API}/admin/tenants/${tid}/trial`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ trial_hasta: fecha }),
      });
      if (res.ok) await cargar(token);
      else { const e = await res.json().catch(() => ({})); alert("Error: " + (e.detail || res.status)); }
    } catch { alert("Error de conexión"); }
  };

  const toggleActivo = async (tid) => {
    await fetch(`${API}/admin/tenants/${tid}/toggle`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}` }
    });
    cargar(token);
  };

  const eliminarTenant = async (tid, nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}"?\n\nEsto borrará TODOS los datos del estudio permanentemente.`)) return;
    if (!window.confirm(`⚠️ ÚLTIMA CONFIRMACIÓN\n\n¿Estás seguro de eliminar "${nombre}" y todos sus datos?\nEsta acción NO se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/tenants/${tid}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) cargar(token);
      else { const d = await res.json(); alert("Error: " + (d.detail || "No se pudo eliminar")); }
    } catch { alert("Error de conexión"); }
  };

  const logout = () => {
    localStorage.removeItem("obras_admin_token");
    setToken(null); setTenants([]); setStats(null);
  };

  const tabStyle = (id) => ({
    padding:"8px 16px", borderRadius:7, border:"none", cursor:"pointer",
    fontFamily:"'Syne',sans-serif", fontWeight:600, fontSize:13,
    background: tab===id ? C.surface : "transparent",
    color: tab===id ? C.text : C.muted,
    boxShadow: tab===id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
  });

  const tenantsFiltrados = tenants.filter(t => {
    const matchSearch = search === "" || t.nombre?.toLowerCase().includes(search.toLowerCase()) || t.email_admin?.toLowerCase().includes(search.toLowerCase());
    const matchPlan = filterPlan === "todos" || t.plan_estado === filterPlan;
    const matchCategoria = filterCategoria === "todas" || (t.categoria || "normal") === filterCategoria;
    return matchSearch && matchPlan && matchCategoria;
  });

  if (!token) return (
    <div style={{ minHeight:"100dvh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Syne',sans-serif" }}>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:48, width:320, boxShadow:"0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.accent, marginBottom:4 }}>FAIM OBRAS</div>
        <div style={{ fontSize:11, color:C.muted, marginBottom:32, fontFamily:"'IBM Plex Mono',monospace", letterSpacing:"2px" }}>PANEL DE ADMINISTRACIÓN</div>
        <div style={{ marginBottom:12 }}>
          <label style={{ display:"block", fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, letterSpacing:"1px", textTransform:"uppercase" }}>Email</label>
          <input value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} type="email"
            style={{ width:"100%", padding:"10px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:"border-box", background:C.surface2, outline:"none" }} />
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ display:"block", fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, letterSpacing:"1px", textTransform:"uppercase" }}>Contraseña</label>
          <input value={loginPass} onChange={e=>setLoginPass(e.target.value)} type="password" onKeyDown={e=>e.key==="Enter"&&loginAdmin()}
            style={{ width:"100%", padding:"10px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:"border-box", background:C.surface2, outline:"none" }} />
        </div>
        {loginError && <div style={{ color:C.red, fontSize:13, marginBottom:12, textAlign:"center" }}>{loginError}</div>}
        <button onClick={loginAdmin} style={{ width:"100%", padding:12, background:C.accent, color:"white", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'Syne',sans-serif" }}>
          Ingresar
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100dvh", background:C.bg, fontFamily:"'Syne',sans-serif" }}>
      {/* Header */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.accent }}>FAIM OBRAS</div>
          <span style={{ fontSize:12, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>Admin Panel</span>
        </div>
        <button onClick={logout} style={{ fontSize:12, color:C.muted, background:"none", border:"none", cursor:"pointer" }}>Cerrar sesión</button>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"24px 16px" }}>

        {/* Stats */}
        {stats && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:24 }}>
            {[
              { label:"Total cuentas", value:stats.total_tenants ?? tenants.length, color:C.accent2 },
              { label:"En trial",      value:stats.en_trial ?? tenants.filter(t=>t.plan_estado==="trial").length, color:C.warn },
              { label:"Activos",       value:stats.activos  ?? tenants.filter(t=>t.plan_estado==="activo").length, color:C.green },
              { label:"Vencidos",      value:stats.vencidos ?? tenants.filter(t=>t.plan_estado==="vencido").length, color:C.red },
              { label:"Tester",        value:stats.por_categoria?.tester ?? tenants.filter(t=>t.categoria==="tester").length, color:C.accent2 },
              { label:"Promo",         value:stats.por_categoria?.promo ?? tenants.filter(t=>t.categoria==="promo").length, color:C.warn },
              { label:"Condicional",   value:stats.por_categoria?.condicional ?? tenants.filter(t=>t.categoria==="condicional").length, color:C.muted },
            ].map(s => (
              <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:C.muted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Embudo de la demo pública */}
        {demoStats && (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"16px 18px", marginBottom:24 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:12 }}>
              Demo pública — faimobras.com/demo
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:16 }}>
              {[
                { label:"Total", d:demoStats.total },
                { label:"Últimos 30 días", d:demoStats.ultimos_30_dias },
                { label:"Últimos 7 días", d:demoStats.ultimos_7_dias },
              ].map(({ label, d }) => (
                <div key={label}>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6 }}>{label}</div>
                  <div style={{ display:"flex", alignItems:"baseline", gap:14 }}>
                    <div>
                      <div style={{ fontSize:22, fontWeight:800, color:C.accent2 }}>{d.visitas}</div>
                      <div style={{ fontSize:10, color:C.muted }}>entraron a la demo</div>
                    </div>
                    <div style={{ fontSize:16, color:C.muted }}>→</div>
                    <div>
                      <div style={{ fontSize:22, fontWeight:800, color:C.green }}>{d.llego_a_pago}</div>
                      <div style={{ fontSize:10, color:C.muted }}>llegaron a la pantalla de pago</div>
                    </div>
                    <div style={{ marginLeft:"auto", fontSize:13, fontWeight:700, color: d.conversion_pct >= 10 ? C.green : C.warn }}>
                      {d.conversion_pct}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:"flex", gap:4, background:C.surface2, borderRadius:8, padding:4, marginBottom:20, width:"fit-content" }}>
          <button style={tabStyle("cuentas")} onClick={()=>setTab("cuentas")}>Cuentas</button>
          <button style={tabStyle("finanzas")} onClick={()=>setTab("finanzas")}>Finanzas</button>
          <button style={tabStyle("precios")} onClick={()=>setTab("precios")}>Precios</button>
          <button style={tabStyle("anuncio")} onClick={()=>setTab("anuncio")}>Anuncio</button>
          <button style={tabStyle("flujo")} onClick={()=>setTab("flujo")}>Flujo</button>
        </div>

        {tab === "anuncio" && <PanelAnuncio token={token} />}

        {tab === "finanzas" && <PanelFinanzas token={token} tenants={tenants} />}

        {tab === "flujo" && (
          <Suspense fallback={
            <div style={{ height: 520, borderRadius: 14, background: "#0d1211",
                          border: "1px solid #1f2825", display: "grid", placeItems: "center",
                          color: "#6f7f78", fontSize: 13 }}>
              Armando el mapa…
            </div>
          }>
            <EsferaFlujo />
          </Suspense>
        )}

        {/* ── CUENTAS ──────────────────────────────────────────────────────────── */}
        {tab === "cuentas" && (
          <>
            <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por nombre o email..."
                style={{ padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:C.surface, outline:"none", flex:1, minWidth:200 }} />
              <select value={filterPlan} onChange={e=>setFilterPlan(e.target.value)}
                style={{ padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:C.surface, outline:"none", cursor:"pointer" }}>
                <option value="todos">Todos los planes</option>
                <option value="trial">Trial</option>
                <option value="activo">Activo</option>
                <option value="vencido">Vencido</option>
              </select>
              <select value={filterCategoria} onChange={e=>setFilterCategoria(e.target.value)}
                style={{ padding:"8px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:C.surface, outline:"none", cursor:"pointer" }}>
                <option value="todas">Todas las categorías</option>
                {CATEGORIAS_TENANT.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <button onClick={()=>cargar(token)} style={{ padding:"8px 16px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, cursor:"pointer", color:C.muted }}>
                Actualizar
              </button>
            </div>

            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1.6fr 0.8fr 1fr 1.1fr 1fr 1.2fr auto", gap:0, borderBottom:`1px solid ${C.border}`, padding:"10px 16px", background:C.surface2 }}>
                {["Estudio","Email","Plan","Categoría","Trial hasta","Usuarios","Precio/mes","Acciones"].map(h => (
                  <div key={h} style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
                ))}
              </div>
              {loading ? (
                <div style={{ padding:32, textAlign:"center", color:C.muted }}>Cargando...</div>
              ) : tenantsFiltrados.length === 0 ? (
                <div style={{ padding:32, textAlign:"center", color:C.muted }}>No hay cuentas</div>
              ) : tenantsFiltrados.map((t, i) => {
                const precioVal = precioEdit[t.id] !== undefined ? precioEdit[t.id] : String(Math.round(t.precio_mensual || 0));
                const precioChanged = parseFloat(precioVal || 0) !== Math.round(t.precio_mensual || 0);
                const usuariosIncluidos = t.usuarios_incluidos ?? 2;
                const usuariosVal = usuariosEdit[t.id] !== undefined ? usuariosEdit[t.id] : String(usuariosIncluidos);
                const usuariosChanged = parseInt(usuariosVal || 0, 10) !== usuariosIncluidos;
                const trialISO = aISO(t.trial_hasta);
                return (
                <div key={t.id} style={{ display:"grid", gridTemplateColumns:"1.4fr 1.6fr 0.8fr 1fr 1.1fr 1fr 1.2fr auto", gap:0, padding:"12px 16px", borderBottom: i < tenantsFiltrados.length-1 ? `1px solid ${C.border}` : "none", alignItems:"center", background: t.activo===false ? "#fef2f2" : "white" }}>
                  <div style={{ fontWeight:600, fontSize:14, color:C.text }}>{t.nombre}</div>
                  <div style={{ fontSize:13, color:C.muted }}>{t.email_admin}</div>
                  <div><Badge plan={t.plan_estado} /></div>
                  <div>
                    <select value={t.categoria || "normal"} onChange={e => cambiarCategoria(t.id, e.target.value)}
                      style={{ fontSize:11, padding:"4px 6px", border:`1px solid ${C.border}`, borderRadius:6, background:C.surface, outline:"none", cursor:"pointer", width:"100%", boxSizing:"border-box" }}>
                      {CATEGORIAS_TENANT.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <input type="date" defaultValue={trialISO}
                      onChange={e => e.target.value && guardarTrial(t.id, e.target.value)}
                      style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace", border:`1px solid ${C.border}`, borderRadius:6, padding:"3px 6px", background:C.surface, outline:"none", width:"100%", boxSizing:"border-box" }} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:C.muted, marginBottom:3 }}>
                      {t.cant_usuarios ?? 0} activo{(t.cant_usuarios ?? 0) === 1 ? "" : "s"}
                    </div>
                    <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                      <span style={{ fontSize:10, color:C.muted, whiteSpace:"nowrap" }}>cupos</span>
                      <input type="number" min={0} value={usuariosVal}
                        onChange={e => setUsuariosEdit(p => ({ ...p, [t.id]: e.target.value }))}
                        style={{ width:40, padding:"3px 5px", border:`1px solid ${usuariosChanged ? C.accent : C.border}`, borderRadius:5, fontSize:11, fontFamily:"'IBM Plex Mono',monospace", outline:"none", boxSizing:"border-box" }} />
                      <button onClick={() => guardarUsuarios(t.id, usuariosVal)} disabled={usuariosSaving[t.id] || !usuariosChanged}
                        style={{ padding:"3px 7px", borderRadius:5, border:"none", fontSize:10, fontWeight:700, cursor: usuariosChanged ? "pointer" : "default", background: usuariosSaved[t.id] ? C.green : usuariosChanged ? C.accent : C.surface2, color: usuariosSaved[t.id] || usuariosChanged ? "white" : C.muted, opacity: usuariosSaving[t.id] ? 0.6 : 1 }}>
                        {usuariosSaved[t.id] ? "✓" : usuariosSaving[t.id] ? "..." : "OK"}
                      </button>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                    <span style={{ fontSize:12, color:C.muted }}>$</span>
                    <input type="number" value={precioVal}
                      onChange={e => setPrecioEdit(p => ({ ...p, [t.id]: e.target.value }))}
                      style={{ width:"100%", minWidth:70, padding:"5px 8px", border:`1px solid ${precioChanged ? C.accent : C.border}`, borderRadius:6, fontSize:12, fontFamily:"'IBM Plex Mono',monospace", outline:"none", boxSizing:"border-box" }} />
                    <button onClick={() => guardarPrecio(t.id, precioVal)} disabled={precioSaving[t.id] || !precioChanged}
                      style={{ padding:"4px 9px", borderRadius:6, border:"none", fontSize:11, fontWeight:700, cursor: precioChanged ? "pointer" : "default", background: precioSaved[t.id] ? C.green : precioChanged ? C.accent : C.surface2, color: precioSaved[t.id] || precioChanged ? "white" : C.muted, opacity: precioSaving[t.id] ? 0.6 : 1, whiteSpace:"nowrap" }}>
                      {precioSaved[t.id] ? "✓" : precioSaving[t.id] ? "..." : "OK"}
                    </button>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    {t.plan_estado !== "activo" && (
                      <button onClick={()=>cambiarPlan(t.id,"activo")}
                        style={{ padding:"4px 10px", fontSize:11, fontWeight:700, background:"#f0fdf4", border:"1px solid #bbf7d0", color:C.green, borderRadius:6, cursor:"pointer" }}>
                        Activar
                      </button>
                    )}
                    {t.plan_estado !== "trial" && (
                      <button onClick={()=>cambiarPlan(t.id,"trial")}
                        style={{ padding:"4px 10px", fontSize:11, fontWeight:700, background:"#fffbeb", border:"1px solid #fde68a", color:C.warn, borderRadius:6, cursor:"pointer" }}>
                        Trial
                      </button>
                    )}
                    <button onClick={()=>toggleActivo(t.id)}
                      style={{ padding:"4px 10px", fontSize:11, fontWeight:700, background:"#f1f5f9", border:"1px solid #e2e8f0", color:"#64748b", borderRadius:6, cursor:"pointer" }}>
                      {t.activo===false ? "Habilitar" : "Suspender"}
                    </button>
                    <button onClick={()=>eliminarTenant(t.id, t.nombre)}
                      style={{ padding:"4px 10px", fontSize:11, fontWeight:700, background:"#fef2f2", border:"1px solid #fecaca", color:C.red, borderRadius:6, cursor:"pointer" }}>
                      Eliminar
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
            <div style={{ fontSize:12, color:C.muted, marginTop:8 }}>{tenantsFiltrados.length} cuenta{tenantsFiltrados.length!==1?"s":""}</div>
          </>
        )}

        {/* ── PRECIOS ──────────────────────────────────────────────────────────── */}
        {tab === "precios" && (
          <div>
            <div style={{ display:"flex", gap:4, background:C.surface2, borderRadius:8, padding:4, marginBottom:20, width:"fit-content" }}>
              {[["mo","Mano de Obra UOCRA"],["hierros","Hierros"],["materiales","Materiales"],["maquinaria","Maquinaria"]].map(([id, label]) => (
                <button key={id} onClick={() => setPreciosTab(id)}
                  style={{ padding:"7px 14px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"'Syne',sans-serif", fontWeight:600, fontSize:13, background:preciosTab===id ? C.surface : "transparent", color:preciosTab===id ? C.text : C.muted, boxShadow:preciosTab===id?"0 1px 3px rgba(0,0,0,0.08)":"none" }}>
                  {label}
                </button>
              ))}
            </div>

            {preciosTab === "mo" && <PanelMO token={token} />}
            {preciosTab === "hierros" && <PanelHierros token={token} />}
            {preciosTab === "materiales" && <PanelMateriales token={token} />}
            {preciosTab === "maquinaria" && <PanelMaquinaria token={token} />}
          </div>
        )}

      </div>
    </div>
  );
}